import { first } from 'lodash';
import { createSelector } from 'reselect';

import { RootState } from 'src/config/rootStore';
import { SharedPreferencesKeys } from '../models/user';

export const getUserInformation = (state: RootState) => state.user;
export const getUserTimeZone = (state: RootState) => state.user.localTimezone;

export const getIsRecheckingEligibility = createSelector(
    getUserInformation,
    ({ hpiMemberInsurance }) => !!hpiMemberInsurance,
);

export const getFullName = createSelector(
    getUserInformation,
    ({ firstName, lastName }) => `${firstName} ${lastName}`,
);

export const getUserId = createSelector(getUserInformation, ({ id }) => id);

export const isUserDataFetched = createSelector(getUserInformation, ({ id }) => id > 0);

export const getMobileAppUsed = createSelector(getUserInformation, user => !!user.mobileAppUsed);

export const getUserInitials = createSelector(getFullName, fullName =>
    fullName.length > 1
        ? fullName
              .split(' ')
              .map(word => word[0]?.toUpperCase())
              .join('')
        : '',
);

export const getAdditionalInfoProvided = createSelector(
    getUserInformation,
    ({ country, availability, languagePreferences, zipCode, city, state }) => {
        if (!country || !availability?.length || !languagePreferences?.length || !city) {
            return false;
        }

        if (country === 'United States') {
            return !!state && !!zipCode;
        }

        return true;
    },
);

export const getMostRecentCareRecommendation = createSelector(
    getUserInformation,
    ({ careRecommendations }) => first(careRecommendations),
);

export const getCoachAssignmentUuid = (providerUuid: string) => (state: RootState) =>
    state.providers.myProviders.find(({ coach }) => coach?.uuid === providerUuid)?.uuid || '';

// normalizes zendesk metadata category payload for 'new category' types
export const getCategorySelections = createSelector(
    getMostRecentCareRecommendation,
    mostRecentCareRec => {
        const topicSelections = mostRecentCareRec?.topicSelections ?? [];
        const categories = [...topicSelections.slice(0, 3)].map(
            ({ topicOfFocus, subtopicSelections }) => {
                return {
                    name: topicOfFocus.name,
                    subcategories: subtopicSelections.map(
                        ({ subtopicOfFocus }) => subtopicOfFocus.name,
                    ),
                    additionalInformation: '',
                };
            },
        );
        return categories;
    },
);

export const getUserPreference = (preferenceKey: SharedPreferencesKeys) =>
    createSelector(getUserInformation, user => {
        return user.sharedPreferences.find(preference => preference.key === preferenceKey)?.value;
    });
